/**
 * Calendar wave 6a public booking-page copy — EN + AR.
 *
 * Same shape as consentCopy.ts: per-key `{ en, ar }` with a typed `t()`
 * helper. The booking page is public + session-free but still consumes
 * `useLocale().isArabic` so the AR pass reaches this surface (real MENA
 * product Arabic, not English strings under `dir="rtl"`).
 */

export type BookingLocale = 'en' | 'ar'

export const BOOKING_COPY = {
  'brand': {
    en: 'WingCaster',
    ar: 'وينغكاستر',
  },
  'page.title': {
    en: 'Book a meeting',
    ar: 'حجز موعد',
  },
  'loading': {
    en: 'Loading',
    ar: 'جارٍ التحميل',
  },
  'unavailable.title': {
    en: 'This booking page is not available.',
    ar: 'هذه الصفحة غير متاحة للحجز.',
  },
  'unavailable.detail': {
    en: 'The link may be incorrect, expired, or the booking link may have been disabled.',
    ar: 'قد يكون الرابط غير صحيح أو منتهي الصلاحية، أو ربما تم إيقاف رابط الحجز.',
  },
  'error.generic.title': {
    en: 'Something went wrong',
    ar: 'حدث خطأ ما',
  },
  'error.generic.detail': {
    en: 'Please refresh the page and try again.',
    ar: 'حدّث الصفحة وحاول مرة أخرى.',
  },
  'header.with': {
    en: 'Book a meeting with',
    ar: 'احجز موعدًا مع',
  },
  'timesInTimezone': {
    en: 'Times shown in {timezone}',
    ar: 'الأوقات معروضة بتوقيت {timezone}',
  },
  'duration.label': {
    en: 'Meeting length',
    ar: 'مدة اللقاء',
  },
  'duration.minutes': {
    en: '{minutes} min',
    ar: '{minutes} دقيقة',
  },
  'dayStrip.label': {
    en: 'Choose a day',
    ar: 'اختر اليوم',
  },
  'slots.label': {
    en: 'Available times',
    ar: 'الأوقات المتاحة',
  },
  'slots.loading': {
    en: 'Checking availability…',
    ar: 'جارٍ التحقق من الأوقات المتاحة…',
  },
  'slots.empty': {
    en: 'No availability in this period.',
    ar: 'لا توجد أوقات متاحة في هذه الفترة.',
  },
  'slots.error': {
    en: 'Could not load availability. Please try another day.',
    ar: 'تعذّر تحميل الأوقات المتاحة. جرّب يومًا آخر.',
  },
  'form.heading': {
    en: 'Your details',
    ar: 'بياناتك',
  },
  'form.name': {
    en: 'Full name',
    ar: 'الاسم الكامل',
  },
  'form.name.required': {
    en: 'Please enter your name.',
    ar: 'يرجى إدخال اسمك.',
  },
  'form.email': {
    en: 'Email address',
    ar: 'البريد الإلكتروني',
  },
  'form.email.required': {
    en: 'Please enter your email address.',
    ar: 'يرجى إدخال بريدك الإلكتروني.',
  },
  'form.email.invalid': {
    en: 'Please enter a valid email address.',
    ar: 'يرجى إدخال بريد إلكتروني صحيح.',
  },
  'form.phone': {
    en: 'Phone (optional)',
    ar: 'الهاتف (اختياري)',
  },
  'form.phone.invalid': {
    en: 'Please enter a phone number in international format, starting with +.',
    ar: 'يرجى إدخال رقم هاتف بالصيغة الدولية، يبدأ بعلامة +.',
  },
  'form.notes': {
    en: 'Notes (optional)',
    ar: 'ملاحظات (اختياري)',
  },
  'form.notes.placeholder': {
    en: 'Anything the host should know?',
    ar: 'هل من شيء يجب أن يعرفه المضيف؟',
  },
  'form.submit': {
    en: 'Confirm booking',
    ar: 'تأكيد الحجز',
  },
  'form.submitting': {
    en: 'Booking…',
    ar: 'جارٍ الحجز…',
  },
  'error.taken': {
    en: 'That slot was just taken — please choose another.',
    ar: 'تم حجز هذا الموعد للتو — يرجى اختيار وقت آخر.',
  },
  'error.rateLimited': {
    en: 'Too many attempts — please try again in a few minutes.',
    ar: 'محاولات كثيرة جدًا — يرجى المحاولة مرة أخرى بعد بضع دقائق.',
  },
  'error.invalid': {
    en: 'Some details need attention — please review the highlighted fields.',
    ar: 'بعض البيانات تحتاج إلى مراجعة — يرجى التحقق من الحقول المحددة.',
  },
  'error.submit': {
    en: 'Could not complete the booking. Please try again.',
    ar: 'تعذّر إتمام الحجز. يرجى المحاولة مرة أخرى.',
  },
  'confirmed.title': {
    en: 'Booking requested',
    ar: 'تم إرسال طلب الحجز',
  },
  'confirmed.detail': {
    en: 'This meeting is pending confirmation — the host confirms every booking, and the confirmation follows via their usual contact channel.',
    ar: 'هذا اللقاء بانتظار التأكيد — يؤكد المضيف كل حجز، ويصلك التأكيد عبر قناة التواصل المعتادة لديه.',
  },
  'confirmed.when': {
    en: 'Requested time',
    ar: 'الوقت المطلوب',
  },
  'confirmed.close': {
    en: 'You can close this page.',
    ar: 'يمكنك إغلاق هذه الصفحة.',
  },
} as const

export type BookingCopyKey = keyof typeof BOOKING_COPY

export function bt(key: BookingCopyKey, locale: BookingLocale): string {
  return BOOKING_COPY[key][locale]
}

/** Copy with `{placeholder}` interpolation, e.g. timezone labels. */
export function btf(key: BookingCopyKey, locale: BookingLocale, vars: Record<string, string>): string {
  return bt(key, locale).replace(/\{(\w+)\}/g, (_, name: string) => vars[name] ?? '')
}
